import { Checkbox, FormControlLabel } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { DateWrapper } from '@trucktrax/trucktrax-common';
import { DriverDto, PermissionAccess, UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { DATASOURCE, DRIVERS_PATH } from '../../../constants/apiConstants';
import {
  ADMIN_KEYS,
  ADMIN_LABELS,
  TRUCKTRAX_GEO_FLAG,
  NOT_AVAILABLE,
} from '../../../constants/appConstants';
import { DRIVERS_TEXT } from '../../../constants/navConstants';
import {
  addDataTest,
  addHeaderDataTest,
  getFullName,
  getRegionUrlToNameMap,
  getUrl,
} from '../../../util/adminUtil';
import { getGeotraxBaseUrl } from '../../../util/apiUtil';
import { isFeatureFlagEnabled } from '../../../util/featureFlagUtil';
import { RegionAccessMap } from '../../../util/permissionUtil';
import { setDataTableQueryParams } from '../../../store/actions/dataTableActions';
import DataTableContent from '../../shared/DataTableContent';
import DriversAddModal from './DriversAddModal';
import styles from './DriversView.module.css';

/* eslint-disable react/no-unstable-nested-components */
export interface DriversViewState {
  isOpen: boolean;
  isCloseRequest: boolean;
  displayInternal: boolean;
  displayExternal: boolean;
}

export class DriversView extends Component<DriversViewProps, DriversViewState> {
  static defaultProps = {
    regions: [],
    currentRegion: { url: '' },
    driversPermissionAccess: '',
    tab: 0,
  };

  static getDisplayStateFromQueryParams = (queryParams?: any) => {
    if (queryParams && queryParams.isExternal === undefined) {
      return { displayInternal: true, displayExternal: true };
    }

    const isExternal = queryParams?.isExternal;
    if (isExternal === 'true') {
      return { displayInternal: false, displayExternal: true };
    }
    if (isExternal === 'false') {
      return { displayInternal: true, displayExternal: false };
    }
    return null;
  };

  constructor(props: DriversViewProps) {
    super(props);
    const displayState = DriversView.getDisplayStateFromQueryParams(props.savedQueryParams);
    this.state = {
      isOpen: false,
      isCloseRequest: false,
      displayInternal: displayState?.displayInternal ?? true,
      displayExternal: displayState?.displayExternal ?? false,
    };
  }

  baseUrl = getGeotraxBaseUrl() + DRIVERS_PATH;

  static getSsoId = (item: DriverDto) => (item?.employeeNumber ? item.employeeNumber : 'Not set');

  commonColumns = () => [
    {
      Header: addHeaderDataTest(null, 'sort-driver-by-name', ADMIN_LABELS.NAME),
      id: ADMIN_KEYS.LAST_NAME,
      accessor: getFullName,
      minWidth: 250,
      Cell: (props: { value: string }) => addDataTest(null, 'admin-driver-name', props.value),
    },
    {
      Header: addHeaderDataTest(null, 'sort-user-by-externalId', ADMIN_LABELS.EXTERNAL_ID),
      id: ADMIN_KEYS.EXTERNAL_ID,
      accessor: ADMIN_KEYS.EXTERNAL_ID,
      minWidth: 130,
      Cell: (props: any) => addDataTest(props, 'admin-user-externalId', props.value),
    },
    {
      Header: addHeaderDataTest(null, 'sort-user-by-ssoId', ADMIN_LABELS.SSO_ID),
      id: ADMIN_KEYS.SSO_ID,
      accessor: DriversView.getSsoId,
      minWidth: 130,
      style: {
        fontStyle: 'italic',
        color: '#878E93',
      },
      Cell: (props: any) => addDataTest(props, 'admin-user-ssoId', props.value),
    },
    {
      Header: addHeaderDataTest(null, 'sort-driver-by-pin', ADMIN_LABELS.PIN),
      accessor: ADMIN_KEYS.PIN,
      minWidth: 100,
      Cell: (props: { value: string }) => addDataTest(null, 'admin-driver-pin', props.value),
    },
    {
      Header: ADMIN_LABELS.REGION,
      id: ADMIN_KEYS.REGION,
      accessor: (row: any) => getRegionUrlToNameMap(this.props.regions)[getUrl(row)!],
      minWidth: 200,
      sortable: false,
    },
  ];

  activeTabColumns = () => [
    ...this.commonColumns(),
    {
      Header: addHeaderDataTest(null, 'sort-driver-by-plant', ADMIN_LABELS.PRIMARY_PLANT),
      id: ADMIN_KEYS.PRIMARY_PLANT,
      accessor: ADMIN_KEYS.PRIMARY_PLANT,
      minWidth: 190,
      Cell: (props: { value: any }) =>
        addDataTest(null, 'admin-driver-primary-plant', props.value && props.value.name),
    },
    {
      Header: addHeaderDataTest(
        null,
        'sort-driver-by-primary-product-line',
        ADMIN_LABELS.PRIMARY_PRODUCT_LINE,
      ),
      id: ADMIN_KEYS.PRIMARY_PRODUCT_LINE,
      accessor: ADMIN_KEYS.PRIMARY_PRODUCT_LINE,
      minWidth: 190,
      Cell: (props: { value: string }) =>
        addDataTest(null, 'admin-driver-default-product-line', props.value || NOT_AVAILABLE),
    },
    {
      Header: addHeaderDataTest(
        null,
        'sort-driver-by-secondary-product-line',
        ADMIN_LABELS.SECONDARY_PRODUCT_LINE,
      ),
      id: ADMIN_KEYS.SECONDARY_PRODUCT_LINE,
      accessor: ADMIN_KEYS.SECONDARY_PRODUCT_LINE,
      minWidth: 190,
      Cell: (props: { value: string }) =>
        addDataTest(null, 'admin-driver-secondary-product-line', props.value || NOT_AVAILABLE),
    },
    ...this.haulerColumns(),
    ...this.truckTraxGeoColumns(),
  ];

  deactivatedTabColumns = () => [
    ...this.commonColumns(),
    ...this.haulerColumns(),
    ...this.truckTraxGeoColumns(),
    {
      Header: addHeaderDataTest(
        null,
        'sort-driver-by-deactivated-date',
        ADMIN_LABELS.DEACTIVATED_DATE,
      ),
      id: ADMIN_KEYS.ARCHIVED_DATE,
      accessor: ADMIN_KEYS.ARCHIVED_DATE,
      minWidth: 100,
      Cell: (props: { value: string }) =>
        addDataTest(
          null,
          'admin-driver-deactivated-date',
          new DateWrapper(props.value).toUSDateString(),
        ),
    },
  ];

  haulerColumns = () => {
    const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
      this.props.userPermission,
      this.props.currentRegion?.url ?? '',
      TRUCKTRAX_GEO_FLAG,
    );

    // Haulers only exist for external drivers, which only exist when TruckTrax Geo is on.
    return isTruckTraxGeoEnabled
      ? [
          {
            Header: addHeaderDataTest(null, 'driver-hauler-header', ADMIN_LABELS.HAULER),
            id: ADMIN_KEYS.HAULERS,
            // A plain string keeps the XLSX export readable: the export calls function
            // accessors directly and blanks NOT_AVAILABLE.
            // The installed @trucktrax/trucktrax-ts-common (1.0.1) predates haulerSummaries, so the
            // row stays untyped until the package is republished.
            accessor: (row: any) =>
              (row.haulerSummaries ?? [])
                .map((hauler: any) => hauler.name)
                .filter(Boolean)
                .join(', ') || NOT_AVAILABLE,
            minWidth: 150,
            sortable: false,
            Cell: (props: { value: string }) =>
              addDataTest(null, 'admin-driver-hauler', props.value),
          },
        ]
      : [];
  };

  truckTraxGeoColumns = () => {
    const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
      this.props.userPermission,
      this.props.currentRegion?.url ?? '',
      TRUCKTRAX_GEO_FLAG,
    );

    // DEV-3856: data-test ids below kept as 'geotrax-lite' to avoid breaking external e2e selectors.
    return isTruckTraxGeoEnabled
      ? [
          {
            Header: addHeaderDataTest(
              null,
              'sort-driver-by-geotrax-lite',
              ADMIN_LABELS.TRUCKTRAX_GEO_ACCESS,
            ),
            id: ADMIN_KEYS.TRUCKTRAX_GEO_ACCESS,
            accessor: ADMIN_KEYS.TRUCKTRAX_GEO_ACCESS,
            minWidth: 170,
            Cell: (props: { value: string }) => {
              const className = cx(
                props.value && 'icon-check',
                !props.value && 'icon-close',
                !props.value && styles.notEnabled,
              );
              return (
                <i aria-hidden="true" data-test="admin-driver-geotrax-lite" className={className} />
              );
            },
          },

          {
            Header: ADMIN_LABELS.EMAIL,
            id: ADMIN_KEYS.EMAIL,
            accessor: ADMIN_KEYS.EMAIL,
            minWidth: 170,
            sortable: false,
            Cell: (props: { value: string }) =>
              addDataTest(null, 'admin-driver-email', props.value),
          },
        ]
      : [];
  };

  getColumns = () =>
    this.props.tab === 1 ? this.deactivatedTabColumns() : this.activeTabColumns();

  requestCloseModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: true,
    });
  };

  closeModal = () => {
    this.setState({
      isOpen: false,
      isCloseRequest: false,
    });
  };

  openModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: false,
    });
  };

  handleTabChange = (_event: any, newValue: number) => {
    if (newValue === 1) {
      this.props.history.push('/admin/drivers/deactivated');
    } else if (newValue === 0) {
      this.props.history.push('/admin/drivers');
    }
  };

  handleDisplayCheckChange = (event: any) => {
    const { displayInternal, displayExternal } = this.state;
    const { target } = event;
    const { name, checked } = target;
    const reverseInternalValue = name === 'displayInternal' && !checked && !displayExternal;
    const reverseExternalValue = name === 'displayExternal' && !checked && !displayInternal;
    const displayList = ['displayInternal', 'displayExternal'];

    // this code is to handle the case when both checkboxes are unchecked and it will keep anyone of them checked always
    if (reverseInternalValue) {
      this.setState(
        {
          displayInternal: checked,
          displayExternal: true,
        },
        this.persistQueryParams,
      );
    } else if (reverseExternalValue) {
      this.setState(
        {
          displayInternal: true,
          displayExternal: checked,
        },
        this.persistQueryParams,
      );
    } else if (displayList.indexOf(name) !== -1) {
      if (name === 'displayInternal') {
        this.setState({ displayInternal: checked }, this.persistQueryParams);
      }

      if (name === 'displayExternal') {
        this.setState({ displayExternal: checked }, this.persistQueryParams);
      }
    }
  };

  getQueryParams = () => {
    const { displayInternal, displayExternal } = this.state;
    let params = {};
    if (Number(displayInternal) + Number(displayExternal) === 1) {
      params = { isExternal: `${displayInternal ? 'false' : 'true'}` };
    }
    return params;
  };

  persistQueryParams = () => {
    this.props.setDataTableQueryParams(this.getQueryParams(), DATASOURCE.DRIVER);
  };

  render() {
    const columns = this.getColumns();
    const hasPermissionAccess = this.props.driversPermissionAccess === PermissionAccess.Edit;
    const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
      this.props.userPermission,
      this.props.currentRegion?.url ?? '',
      TRUCKTRAX_GEO_FLAG,
    );

    const tabs = (
      <Tabs
        value={this.props.tab}
        onChange={this.handleTabChange}
        classes={{
          root: styles.tabsContainer,
        }}
      >
        <Tab label="Active" data-test="drivers-active-tab" />
        <Tab label="Deactivated" data-test="drivers-deactivated-tab" />
      </Tabs>
    );

    const headerRightControls = isTruckTraxGeoEnabled ? (
      <>
        <span className={styles.checkboxTitle}>Driver:</span>
        <FormControlLabel
          data-test="driver-type-filter-internal"
          control={
            <Checkbox
              checked={this.state.displayInternal}
              onChange={this.handleDisplayCheckChange}
              name="displayInternal"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>Internal</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />
        <FormControlLabel
          data-test="driver-type-filter-external"
          control={
            <Checkbox
              checked={this.state.displayExternal}
              onChange={this.handleDisplayCheckChange}
              name="displayExternal"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>External</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />
      </>
    ) : undefined;

    const props: any = {
      baseUrl: this.baseUrl,
      columns,
      header: DRIVERS_TEXT,
      renderTabs: tabs,
      headerRightControls,
      isArchived: this.props.tab === 1,
      canAddNewItem: hasPermissionAccess,
      bypassCache: true,
      queryParams: this.getQueryParams(),
      source: DATASOURCE.DRIVER,
      enableExport: true,
      exportedFileName: 'drivers',
    };
    return (
      <div className={styles.container}>
        <DataTableContent {...props} />
        {hasPermissionAccess && (
          <DriversAddModal
            isOpen={this.state.isOpen}
            openModal={this.openModal}
            closeModal={this.requestCloseModal}
            regions={this.props.regions}
            discardAcceptAction={this.closeModal}
            discardRejectAction={this.openModal}
            isCloseRequest={this.state.isCloseRequest}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  regions: state.assignedRegionList,
  driversPermissionAccess: state.adminPermissionAccess[DRIVERS_TEXT],
  currentRegion: state.currentRegion ?? { url: '' },
  userPermission: state.userPermission || {},
  savedQueryParams: state.dataTableQueryParams?.[DATASOURCE.DRIVER]?.queryParams,
});

interface History {
  push: (p: string) => void;
}

export interface DriversViewProps {
  regions: any[];
  currentRegion?: UrlKeyDto;
  driversPermissionAccess?: string;
  tab: number;
  history: History;
  userPermission: RegionAccessMap;
  savedQueryParams?: any;
  setDataTableQueryParams: (queryParams: any, source: string) => void;
}

export default connect(mapStateToProps, { setDataTableQueryParams })(DriversView);
